/**
 * Route guards.
 *
 * `RequireUser`  — must be signed in (user or ephemeral demo). Otherwise
 *                  redirect to `/sign-in`.
 * `RequireAdmin` — must be a system admin. Non-admin (or unauthenticated)
 *                  is redirected to `/my-documents` (never told the admin
 *                  route exists).
 * `RequireAnon`  — must NOT be signed in. Wraps auth surfaces so a
 *                  signed-in user is bounced to `/my-documents` instead
 *                  of being asked to sign in again.
 */

import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useSession } from '../../hooks/useSession';

export function RequireUser({ children }: { children: ReactNode }) {
  const { loading, session } = useSession();
  const location = useLocation();
  if (loading) return <LoadingShell />;
  if (!session) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/sign-in?redirect=${redirect}`} replace />;
  }
  return <>{children}</>;
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const { loading, isAdmin } = useSession();
  if (loading) return <LoadingShell />;
  if (!isAdmin) return <Navigate to="/my-documents" replace />;
  return <>{children}</>;
}

export function RequireAnon({ children }: { children: ReactNode }) {
  const { loading, user } = useSession();
  if (loading) return <LoadingShell />;
  if (user) return <Navigate to="/my-documents" replace />;
  return <>{children}</>;
}

function LoadingShell() {
  // Quiet, content-shaped placeholder. Avoids a "flash of redirect" while
  // the session check round-trips.
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--fg-3)',
        fontSize: 13,
      }}
    >
      Loading…
    </div>
  );
}
